import React from "react";
import {
  Control,
  Controller,
  useController,
  DeepMap,
  FieldError,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import cx from "classnames";
import dayjs from "dayjs";
import stylesV2 from "./InsuranceForm.module.scss";
import stylesV3 from "./InsuranceFormV2.module.scss";
import { useSchedulingABVariant } from "../../../scheduling/utils/useSchedulingABVariant";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { InsuranceFormData, RelationToPatient } from "features/checkIn/types";
import { Fieldset } from "features/checkIn/components/Fieldset";
import { Introduction } from "features/checkIn/components/Introduction";
import { MaskedDateInput } from "components/MaskedTextInput/MaskedTextInput";
import { TextInput } from "components/TextInput";
import { Typeahead } from "components/Typeahead";
import { RadioButtonGroup, RadioButton } from "components/RadioButton";
import { isValidDateOfBirth } from "utils/dateOfBirthValidation";
import { presenceValidation } from "utils/presenceValidation";
import { PasswordInput } from "components/PasswordInput";
import { InfoTooltip } from "components/InfoTooltip";
import { StateOptions } from "features/checkIn/constants";
import { sanitizeCityInput, sanitizeZipCodeInput } from "utils/addressInput";
import { loadGoogleMapsPlacesApi } from "utils/loadGoogleMapsPlacesApi";

const subscriberNameValidation = {
  required: "Required",
  pattern: {
    value: /^[A-Za-z -]+$/,
    message: "Only letters and - are allowed",
  },
};

const sanitizeSubscriberName = (value: string) =>
  value.replace(/[^A-Za-z -]/g, "");

export const InsuranceForm: React.FC<{
  register: UseFormRegister<Partial<InsuranceFormData>>;
  control: Control<Partial<InsuranceFormData>>;
  insuranceCarriers: Array<string> | null;
  isRemoveBtn: boolean;
  setValue: UseFormSetValue<Partial<InsuranceFormData>>;
  errors: DeepMap<Partial<InsuranceFormData>, FieldError>;
  watch: UseFormWatch<Partial<InsuranceFormData>>;
  onSubmit: () => void;
  formId: string;
}> = ({
  register,
  control,
  insuranceCarriers,
  isRemoveBtn,
  setValue,
  errors,
  onSubmit,
  watch,
  formId,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const subscriberIdTip = (
    <ul className={styles.guideList}>
      <li className={styles.guideListItem}>
        Your ID may also be called: Member ID, Client ID, Enrollee ID, Payer ID,
        Member Claims Number, etc.
      </li>
      <li className={styles.guideListItem}>Some policies may use your SSN as your ID.</li>
    </ul>
  );

  const subscriberNameTip = (
    <ul className={styles.guideList}>
      <li className={styles.guideListItem}>Enter the name of the primary policy holder.</li>
      <li className={styles.guideListItem}>You may be able to reference this detail on a card (if provided).</li>
    </ul>
  );
  const { currentPatient } = useCurrentPatient();
  const [isOpen, setOpen] = React.useState(false);
  const envReact = env_react !== "undefined" ? env_react : "production";
  const apiKey =
    envReact === "development"
      ? "AIzaSyBZoc3kW6Ygemp-m3hsaX4v-H4BDMvHDPo"
      : "AIzaSyDxt1Xdg78h4jTetBtXEt98ZlBnQv5vbHI";
  const [isVisible, setVisible] = React.useState(false);
  const searchInsuranceAddress = React.useRef(null);
  // load google map api js
  const extractAddress = (
    place: any,
    searchInsuranceAddress: { current: { value: string } }
  ) => {
    let address1 = "";
    place.address_components.forEach(
      (component: { types: string; long_name: string; short_name: string }) => {
        const types = component.types;
        const value = component.long_name;
        if (types.includes("street_number")) {
          address1 = address1 + value;
        }
        if (types.includes("route")) {
          address1 = address1 + " " + value;
        }
        if (types.includes("locality")) {
          setValue("city", sanitizeCityInput(value));
        }

        if (types.includes("administrative_area_level_1")) {
          stateField.value = component.short_name;
          setValue("state", component.short_name);
        }

        if (types.includes("postal_code")) {
          setValue("zipCode", sanitizeZipCodeInput(value));
        }
      }
    );
    searchInsuranceAddress.current.value = address1;
    setValue("address1", address1);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeAddress = (
    autocomplete: google.maps.places.Autocomplete,
    searchInsuranceAddress: any
  ) => {
    const place = autocomplete.getPlace();
    extractAddress(place, searchInsuranceAddress);
  };
  // init gmap script
  const initMapScript = React.useCallback(() => {
    return loadGoogleMapsPlacesApi(apiKey);
  }, [apiKey]);

  const { field: insuranceField } = useController({
    rules: { required: true },
    name: "insuranceCarrier",
    control,
  });

  React.useEffect(() => {
    if (isVisible) {
      initMapScript().then(() => {
        if (!searchInsuranceAddress.current || !apiKey) return;

        const options = {
          componentRestrictions: { country: "us" },
        };
        const autocomplete = new window.google.maps.places.Autocomplete(
          searchInsuranceAddress.current,
          options
        );
        autocomplete.setFields([
          "address_component",
          "geometry",
          "formatted_address",
        ]);
        autocomplete.addListener("place_changed", () =>
          onChangeAddress(autocomplete, searchInsuranceAddress)
        );
      });
    }
  }, [apiKey, initMapScript, onChangeAddress, isVisible]);

  const subscriberFirstName = register(
    "subscriberFirstName",
    subscriberNameValidation
  );
  const subscriberLastName = register(
    "subscriberLastName",
    subscriberNameValidation
  );
  const otherInsuranceName = register("otherInsuranceName", {
    required: { value: isOpen, message: "Not a valid Insurance" },
    pattern: { value: /^[a-z / A-Z]*$/, message: "Not a valid Insurance" },
  });
  const subscriberId = register("subscriberId", presenceValidation("Required"));
  const subscriberDateOfBirth = register("subscriberDateOfBirth", {
    required: "Required",
    validate: isValidDateOfBirth,
  });
  const yesPrimaryPolicyHolder = register("isPrimaryPolicyHolder");
  const noPrimaryPolicyHolder = register("isPrimaryPolicyHolder");

  const isPrimaryPolicyHolder = watch("isPrimaryPolicyHolder");
  const [isInsuranceName, setIsInsuranceName] = React.useState("yes");
  const subscriberSsn = register("subscriberSsn", {
    required: insuranceField.value === "VSP" ? true : false,
    pattern: { value: /^\d{4}$/, message: "Must have 4 digits" },
  });

  const subscriptionSex = register("subscriberBirthSex", {
    required: isPrimaryPolicyHolder === "no" ? "Required" : false,
  });

  const address1 = register(
    "address1",
    isPrimaryPolicyHolder === "no"
      ? presenceValidation("Not a valid address")
      : { required: false }
  );
  const address2 = register("address2");
  const city = register(
    "city",
    { required: isPrimaryPolicyHolder === "no" ? "Not a valid city" : false }
    //presenceValidation("Not a valid city")
  );
  const zipCode = register("zipCode", {
    required: isPrimaryPolicyHolder === "no" ? "Required" : false,
    //validate: isValidZip,
  });
  const { field: stateField } = useController({
    rules: { required: isPrimaryPolicyHolder === "no" ? "Required" : false },
    name: "state",
    control,
  });

  const formattedDateOfBirth = dayjs(currentPatient?.dateOfBirth).format(
    "MM/DD/YYYY"
  );

  insuranceField.onChange = (value: string) => {
    if (value === "Other Insurance") {
      setOpen(true);
      // setValue("otherInsuranceName", "");
    } else {
      setOpen(false);
      setValue("otherInsuranceName", undefined);
      if (value !== "VSP") setValue("subscriberSsn", "");
    }
    setValue("insuranceCarrier", value);
    insuranceField.value = value;
  };
  const handleSameNameChoice = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "yes") {
      setValue("subscriberFirstName", currentPatient?.firstName || "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberLastName", currentPatient?.lastName || "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue("subscriberFirstName", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberLastName", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setIsInsuranceName(event.target.value);
  };

  const handlePrimaryPolicyHolderChoice = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value === "yes") {
      setValue("subscriberDateOfBirth", formattedDateOfBirth, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberFirstName", currentPatient?.firstName || "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberLastName", currentPatient?.lastName || "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("relationToPatient", RelationToPatient.Self, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue("subscriberDateOfBirth", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberFirstName", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("subscriberLastName", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("relationToPatient", undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setValue("subscriberId", "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSubscriberNameChange =
    (fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target.value = sanitizeSubscriberName(event.target.value);
      fieldOnChange(event);
    };

  const handleChangeaddress1 = (event: { target: { value: any } }) => {
    if (!isVisible) {
      if (event.target.value.length > 5) setVisible(true);
    }
    setValue("address1", event.target.value);
  };

  const handleChangeCity = (event: { target: { value: string } }) => {
    const value = sanitizeCityInput(event.target.value);
    event.target.value = value;
    setValue("city", value);
  };

  const handleChangeZip = (event: { target: { value: string } }) => {
    const value = sanitizeZipCodeInput(event.target.value);
    event.target.value = value;
    setValue("zipCode", value);
  };

  React.useEffect(() => {
    if (insuranceField.value === "Other Insurance") {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [insuranceField.value]);

  return (
    <form onSubmit={onSubmit} id={formId}>
      <Introduction header="Apply your coverage">
        We are committed to helping you get the most out of your insurance.
      </Introduction>
      <div className={styles.content}>
        <Fieldset
          legend="What Insurance do you have?"
          aside={"(Medical or Vision)"}
        >
          <Typeahead
            field={insuranceField}
            name="insuranceCarrier"
            options={(insuranceCarriers || []).map((carrier) => ({
              label: carrier,
              value: carrier,
            }))}
            label="Insurance Carrier"
            creatable={false}
            clearable={true}
            isClear={true}
            errorMessage={errors.insuranceCarrier?.message}
            errorClassName={cx({
              [styles.emptyError]: !errors.insuranceCarrier?.message,
            })}
          />
        </Fieldset>
        {isOpen && (
          <Fieldset legend="Insurance Name displayed on card">
            {/* other insurance field, working on hidden until selected*/}
            <TextInput
              {...otherInsuranceName}
              inputType="text"
              name="otherInsuranceName"
              // guidedTooltip={otherInsuranceNameTip}
              errorMessage={errors.otherInsuranceName?.message}
              placeholder="Insurance Carrier Name"
              errorClassName={cx({
                [styles.emptyError]: !errors.otherInsuranceName?.message,
              })}
            >
              Insurance Name
            </TextInput>
          </Fieldset>
        )}
        {!isV3 && <hr className={styles.hr} />}
        <Fieldset
          legend="Are you the Subscriber?"
          legendClassName={styles.alwaysVisible}
          aside={"(Primary Policy Holder)"}
          data-testid="primaryRadioGroup"
        >
          <RadioButtonGroup className={styles.buttonGroup}>
            <RadioButton
              {...yesPrimaryPolicyHolder}
              className={styles.radioButton}
              onChange={(e) => {
                yesPrimaryPolicyHolder.onChange(e);
                handlePrimaryPolicyHolderChoice(e);
              }}
              value="yes"
            >
              YES
            </RadioButton>
            <RadioButton
              {...noPrimaryPolicyHolder}
              className={styles.radioButton}
              onChange={(e) => {
                noPrimaryPolicyHolder.onChange(e);
                handlePrimaryPolicyHolderChoice(e);
              }}
              value="no"
            >
              NO
            </RadioButton>
          </RadioButtonGroup>
        </Fieldset>
        <div>
          {isPrimaryPolicyHolder === "no" && (
            <section>
              <Fieldset
                legend="What’s the Subscriber’s information?"
                legendClassName={styles.alwaysVisible}
              >
                <div className={styles.desktopTwoColumn}>
                  <TextInput
                    {...subscriberFirstName}
                    inputType="text"
                    guidedTooltip={subscriberNameTip}
                    onChange={handleSubscriberNameChange(
                      subscriberFirstName.onChange
                    )}
                    errorMessage={errors.subscriberFirstName?.message}
                    placeholder="First Name"
                    errorClassName={cx({
                      [styles.emptyError]: !errors.subscriberFirstName?.message,
                    })}
                  >
                    Subscriber First Name
                  </TextInput>

                  <TextInput
                    {...subscriberLastName}
                    inputType="text"
                    guidedTooltip={subscriberNameTip}
                    onChange={handleSubscriberNameChange(
                      subscriberLastName.onChange
                    )}
                    errorMessage={errors.subscriberLastName?.message}
                    placeholder="Last Name"
                    errorClassName={cx({
                      [styles.emptyError]: !errors.subscriberLastName?.message,
                    })}
                  >
                    Subscriber Last Name
                  </TextInput>
                </div>
                <div className={styles.desktopTwoColumn}>
                  <MaskedDateInput
                    {...subscriberDateOfBirth}
                    name="subscriberDateOfBirth"
                    setValue={setValue}
                    errorMessage={errors.subscriberDateOfBirth?.message}
                    errorClassName={cx({
                      [styles.emptyError]:
                        !errors.subscriberDateOfBirth?.message,
                    })}
                  >
                    Subscriber Date of Birth
                  </MaskedDateInput>

                  <TextInput
                    {...subscriberId}
                    inputType="text"
                    guidedTooltip={subscriberIdTip}
                    errorMessage={errors.subscriberId?.message}
                    errorClassName={cx({
                      [styles.emptyError]: !errors.subscriberId?.message,
                    })}
                  >
                    Subscriber ID
                  </TextInput>
                </div>
              </Fieldset>
              <Fieldset
                legend="Subscriber Birth Sex"
                legendClassName={cx(styles.alwaysVisible)}
              >
                <RadioButtonGroup
                  className={styles.buttonGroup}
                  errorMessage={errors.subscriberBirthSex?.message}
                >
                  <RadioButton
                    {...subscriptionSex}
                    className={styles.radioButton}
                    value="Male"
                  >
                    Male
                  </RadioButton>
                  <RadioButton
                    {...subscriptionSex}
                    className={styles.radioButton}
                    value="Female"
                  >
                    Female
                  </RadioButton>
                </RadioButtonGroup>
              </Fieldset>
              <Fieldset
                legend="Subscriber Address"
                legendClassName={cx(styles.alwaysVisible)}
              >
                <TextInput
                  {...address1}
                  ref={searchInsuranceAddress}
                  inputType="text"
                  onChange={(e) => {
                    handleChangeaddress1(e);
                  }}
                  errorMessage={errors.address1?.message}
                  className={cx(styles.field, styles.fieldSpan)}
                  errorClassName={cx({
                    [styles.emptyError]: !errors.address1?.message,
                  })}
                >
                  Subscriber Address
                </TextInput>

                <TextInput
                  {...address2}
                  inputType="text"
                  errorMessage={errors.address2?.message}
                  className={cx(styles.field, styles.fieldSpan)}
                  errorClassName={cx({
                    [styles.emptyError]: !errors.address2?.message,
                  })}
                >
                  Subscriber Apt./Suite # (optional)
                </TextInput>
                <Typeahead
                  field={stateField}
                  name="state"
                  options={StateOptions}
                  label="Subscriber State"
                  creatable={false}
                  clearable={true}
                  errorMessage={errors.state?.message}
                  className={cx(styles.field, styles.fieldSpan)}
                  errorClassName={cx({
                    [styles.emptyError]: !errors.state?.message,
                  })}
                />
                <div className={styles.desktopTwoColumn}>
                  <TextInput
                    {...city}
                    inputType="text"
                    onChange={handleChangeCity}
                    errorMessage={errors.city?.message}
                    className={styles.field}
                    errorClassName={cx({
                      [styles.emptyError]: !errors.city?.message,
                    })}
                  >
                    Subscriber City
                  </TextInput>
                  <TextInput
                    {...zipCode}
                    inputType="text"
                    inputMode="numeric"
                    maxLength={5}
                    onChange={handleChangeZip}
                    errorMessage={errors.zipCode?.message}
                    className={styles.field}
                    errorClassName={cx({
                      [styles.emptyError]: !errors.zipCode?.message,
                    })}
                  >
                    Subscriber Zip
                  </TextInput>
                </div>
              </Fieldset>
              <Fieldset
                legend="What’s the Subscriber’s relation to the patient?"
                legendClassName={styles.alwaysVisible}
              >
                <Controller
                  rules={{ required: "Required" }}
                  render={({ field, fieldState }) => (
                    <Typeahead
                      field={field}
                      name="relationToPatient"
                      label="Subscriber Relation to Patient"
                      placeholder="Select relationship"
                      creatable={false}
                      options={[
                        { label: "Spouse", value: RelationToPatient.Spouse },
                        {
                          label: "Parent / Guardian",
                          value: RelationToPatient.ParentGuardian,
                        },
                        {
                          label: "Domestic Partner",
                          value: RelationToPatient.DomesticPartner,
                        },
                      ]}
                      clearable={false}
                      errorMessage={fieldState.error?.message}
                      errorClassName={cx({
                        [styles.emptyError]: !fieldState.error?.message,
                      })}
                    />
                  )}
                  name="relationToPatient"
                  control={control}
                />
              </Fieldset>
              {insuranceField.value === "VSP" && (
                <Fieldset
                  legend="What’s the Subscriber’s Social Security Number?"
                  legendClassName={styles.alwaysVisible}
                  fieldGroupClassName={styles.fieldGroup}
                >
                  <div className={styles.fieldWithTooltip}>
                    <div className={styles.ssnContainer}>
                      <PasswordInput
                        {...subscriberSsn}
                        className={styles.ssnField}
                        errorMessage={errors.subscriberSsn?.message}
                        placeholder="####"
                      >
                        Last 4 digits of SSN
                      </PasswordInput>
                    </div>
                    <div className={styles.tooltipContainer}>
                      <span className={styles.tooltipLabel}>
                        Why is this needed?
                      </span>
                      <InfoTooltip
                        className={styles.tooltip}
                        iconClassName={styles.tooltipCloseIcon}
                      >
                        <p>
                          We may use the last four digits of your SSN to
                          validate medical or vision insurance.
                        </p>
                      </InfoTooltip>
                    </div>
                  </div>
                </Fieldset>
              )}
            </section>
          )}
          {isPrimaryPolicyHolder === "yes" && (
            <section>
              <Fieldset
                legend="Does this name match what appears on your insurance?"
                legendClassName={styles.alwaysVisible}
              >
                <div className={styles.readOnlyTwoColumn}>
                  <TextInput
                    errorClassName={styles.emptyError}
                    name="readOnlyFirstName"
                    readOnly={true}
                    inputType="text"
                    defaultValue={currentPatient?.firstName || ""}
                  >
                    First Name
                  </TextInput>
                  <TextInput
                    errorClassName={styles.emptyError}
                    name="readOnlyLastName"
                    readOnly={true}
                    inputType="text"
                    defaultValue={currentPatient?.lastName || ""}
                  >
                    Last Name
                  </TextInput>
                </div>
                <div data-testid="nameRadioButtonGroup">
                  <RadioButtonGroup className={styles.buttonGroup}>
                    <RadioButton
                      name="isInsuranceName"
                      className={styles.radioButton}
                      onChange={handleSameNameChoice}
                      value="yes"
                      checked={isInsuranceName === "yes"}
                    >
                      Yes
                    </RadioButton>
                    <RadioButton
                      name="isInsuranceName"
                      className={styles.radioButton}
                      onChange={handleSameNameChoice}
                      value="no"
                      checked={isInsuranceName === "no"}
                    >
                      No
                    </RadioButton>
                  </RadioButtonGroup>
                </div>
                {isInsuranceName === "no" && (
                  <>
                    Enter the name as it appears on your insurance.
                    <div
                      className={styles.desktopTwoColumn}
                      data-testid="subscriberName"
                    >
                      <TextInput
                        {...subscriberFirstName}
                        inputType="text"
                        guidedTooltip={subscriberNameTip}
                        onChange={handleSubscriberNameChange(
                          subscriberFirstName.onChange
                        )}
                        errorMessage={errors.subscriberFirstName?.message}
                        placeholder="First Name"
                        errorClassName={cx({
                          [styles.emptyError]:
                            !errors.subscriberFirstName?.message,
                        })}
                      >
                        First Name
                      </TextInput>
                      <TextInput
                        {...subscriberLastName}
                        inputType="text"
                        guidedTooltip={subscriberNameTip}
                        onChange={handleSubscriberNameChange(
                          subscriberLastName.onChange
                        )}
                        errorMessage={errors.subscriberLastName?.message}
                        placeholder="Last Name"
                        errorClassName={cx({
                          [styles.emptyError]:
                            !errors.subscriberLastName?.message,
                        })}
                      >
                        Last Name
                      </TextInput>
                    </div>
                  </>
                )}
              </Fieldset>
              <Fieldset
                legend="What’s your date of birth?"
                legendClassName={styles.alwaysVisible}
              >
                <MaskedDateInput
                  {...subscriberDateOfBirth}
                  name="subscriberDateOfBirth"
                  setValue={setValue}
                  errorMessage={errors.subscriberDateOfBirth?.message}
                  errorClassName={cx({
                    [styles.emptyError]: !errors.subscriberDateOfBirth?.message,
                  })}
                >
                  Date of Birth
                </MaskedDateInput>
              </Fieldset>
              <Fieldset
                legend="What’s your subscriber ID?"
                legendClassName={styles.alwaysVisible}
              >
                <TextInput
                  {...subscriberId}
                  inputType="text"
                  guidedTooltip={subscriberIdTip}
                  errorMessage={errors.subscriberId?.message}
                  errorClassName={cx({
                    [styles.emptyError]: !errors.subscriberId?.message,
                  })}
                >
                  ID
                </TextInput>
              </Fieldset>
              <Fieldset
                legend="What’s the Subscriber’s relation to the patient?"
                legendClassName={styles.alwaysVisible}
              >
                <Controller
                  render={({ field, fieldState }) => (
                    <Typeahead
                      field={field}
                      name="relationToPatient"
                      label="Subscriber Relation to Patient"
                      placeholder="Self (Default)"
                      creatable={false}
                      options={[
                        {
                          label: "Self (Default)",
                          value: RelationToPatient.Self,
                        },
                        { label: "Spouse", value: RelationToPatient.Spouse },
                        {
                          label: "Parent / Guardian",
                          value: RelationToPatient.ParentGuardian,
                        },
                        {
                          label: "Domestic Partner",
                          value: RelationToPatient.DomesticPartner,
                        },
                        {
                          label: "Self (Student Coverage)",
                          value: RelationToPatient.Student,
                        },
                      ]}
                      clearable={false}
                      errorClassName={cx({
                        [styles.emptyError]: !fieldState.error?.message,
                      })}
                    />
                  )}
                  name="relationToPatient"
                  defaultValue={RelationToPatient.Self}
                  control={control}
                />
              </Fieldset>
              {insuranceField.value === "VSP" && (
                <Fieldset
                  legend="What’s your Social Security Number?"
                  legendClassName={styles.alwaysVisible}
                  fieldGroupClassName={styles.fieldGroup}
                >
                  <div className={styles.fieldWithTooltip}>
                    <div className={styles.ssnContainer}>
                      <PasswordInput
                        {...subscriberSsn}
                        inputMode="numeric"
                        className={styles.ssnField}
                        errorMessage={errors.subscriberSsn?.message}
                        placeholder="####"
                      >
                        Last 4 digits of SSN
                      </PasswordInput>
                    </div>
                    <div className={styles.tooltipContainer}>
                      <span className={styles.tooltipLabel}>
                        Why is this needed?
                      </span>
                      <InfoTooltip
                        className={styles.tooltip}
                        iconClassName={styles.tooltipCloseIcon}
                      >
                        <p>
                          We may use the last four digits of your SSN to
                          validate medical or vision insurance.
                        </p>
                      </InfoTooltip>
                    </div>
                  </div>
                </Fieldset>
              )}
            </section>
          )}
        </div>
        {isRemoveBtn && <div>Remove</div>}
      </div>
    </form>
  );
};
